/**
 * Announce — admin envia notificação em massa.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Megaphone, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usersApi } from "@/lib/api-users";
import { massNotifyApi } from "@/lib/api-bulk";

export function AnnouncePage() {
  const [target, setTarget] = useState<"all" | "role" | "specific">("all");
  const [role, setRole] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");

  const stats = useQuery({
    queryKey: ["users-count"],
    queryFn: () => usersApi.list({ size: 1 }),
  });

  const send = useMutation({
    mutationFn: () =>
      massNotifyApi.broadcast({
        target,
        role: target === "role" ? role : undefined,
        title,
        body: body || undefined,
        link: link || undefined,
      }),
    onSuccess: (data) => {
      toast.success(`Enviado pra ${data.recipients} usuário(s)`);
      setTitle("");
      setBody("");
      setLink("");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  return (
    <>
      <PageHeader
        title="Comunicado"
        description="Notifique usuários da plataforma em massa."
        breadcrumbs={[
          { label: "Configurações", to: "/settings" },
          { label: "Comunicado" },
        ]}
      />

      <div className="max-w-2xl space-y-4">
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-6">
          <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-amber-600" /> Compor mensagem
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Aparece como notificação no sino dos destinatários.
          </p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Destinatários</Label>
              <Select value={target} onValueChange={(v) => setTarget(v as typeof target)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os usuários ativos ({stats.data?.total ?? "?"})</SelectItem>
                  <SelectItem value="role">Por papel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {target === "role" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Papel</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="operator">Operador</SelectItem>
                    <SelectItem value="financial">Financeiro</SelectItem>
                    <SelectItem value="client">Cliente</SelectItem>
                    <SelectItem value="nerd">Técnico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Título *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={256} />
              <p className="text-[11px] text-muted-foreground">{title.length}/256</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Mensagem</Label>
              <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Link de destino (opcional)</Label>
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="/jobs/abc-123" />
            </div>
          </div>

          <div className="mt-5 flex justify-end items-center gap-2 pt-4 border-t border-slate-200/60 dark:border-white/10">
            <Badge variant="warning" dot>
              {target === "all" ? `${stats.data?.total ?? 0} usuários` : `Papel ${role || "—"}`}
            </Badge>
            <Button
              onClick={() => send.mutate()}
              disabled={!title || (target === "role" && !role) || send.isPending}
            >
              <Send className="h-4 w-4" />
              {send.isPending ? "Enviando..." : "Enviar agora"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
